import { useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { ReactComponent as BackrsWithText } from '@backrs_ui/common-ui/src/assets/images/backrs-with-text.svg';

import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, Checkbox, FormControlLabel, Typography, Theme, SxProps } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

import { TermsAndConditions } from './TermsAndConditions';
import { BackrsForm } from '../BackrsForm/BackrsForm';
import { BackrsFormField, BackrsFormValues } from '../BackrsForm/types';
import { RegistrationValues } from '../Login/login.util';

const REGISTER_STRINGS = appStrings.modules.register;

type RegisterProps = Readonly<{
  handleRegistration: (data: RegistrationValues) => void;
  errorMessage?: string;
  registerId?: string | null;
  preFilledData?: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}>;

export const Register = ({
  handleRegistration,
  errorMessage,
  registerId,
  preFilledData,
}: RegisterProps) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const [isRegistering, setIsRegistering] = useState(false);

  const defaultRegistrationValues: BackrsFormValues = {
    first_name: preFilledData?.first_name ?? '',
    last_name: preFilledData?.last_name ?? '',
    phone: '',
    email: preFilledData?.email ?? '',
    password: '',
    invite_code: '',
    /** is_fourteen_plus will be converted from a string to a boolean when submitted to handleSubmission
     * @property {string} is_fourteen_plus
     * will be converted to
     * @property {boolean} is_fourteen_plus
     */
    is_fourteen_plus: '',
    ...(registerId && { register_id: registerId }),
  };

  const methods = useForm<BackrsFormValues>({
    defaultValues: defaultRegistrationValues,
  });

  const fields: BackrsFormField[] = [
    {
      id: 'first_name',
      name: 'first_name',
      placeholder: REGISTER_STRINGS.firstNamePlaceholder,
      type: 'text',
      autoComplete: 'firstName',
      autoFocus: true,
      rules: {
        required: REGISTER_STRINGS.error.firstNameRequired,
      },
    },
    {
      id: 'last_name',
      name: 'last_name',
      placeholder: REGISTER_STRINGS.lastNamePlaceholder,
      type: 'text',
      autoComplete: 'lastName',
      rules: {
        required: REGISTER_STRINGS.error.lastNameRequired,
      },
    },
    {
      id: 'phone',
      name: 'phone',
      placeholder: REGISTER_STRINGS.phonePlaceholder,
      type: 'tel',
      autoComplete: 'phone',
      rules: {
        required: REGISTER_STRINGS.error.phoneRequired,
        pattern: { value: /^\d{10}$/, message: REGISTER_STRINGS.error.invalidPhone },
      },
    },
    {
      id: 'email',
      name: 'email',
      placeholder: REGISTER_STRINGS.emailPlaceholder,
      type: 'text',
      autoComplete: 'email',
      rules: {
        required: REGISTER_STRINGS.error.emailRequired,
        pattern: { value: /^\S+@\S+$/i, message: REGISTER_STRINGS.error.invalidEmail },
      },
    },
    {
      id: 'password',
      name: 'password',
      placeholder: REGISTER_STRINGS.passwordPlaceholder,
      type: isPasswordVisible ? 'text' : 'password',
      autoComplete: 'current-password',
      icon: {
        type: 'RIGHT_ICON',
        rightIcon: isPasswordVisible ? 'EyeOff' : 'Eye',
        onRightIconClick: () => {
          setIsPasswordVisible((prev) => !prev);

          setTimeout(() => {
            setIsPasswordVisible(false);
          }, 5000);
        },
      },
      rules: { required: REGISTER_STRINGS.error.passwordRequired },
    },
    {
      id: 'invite_code',
      name: 'invite_code',
      placeholder: REGISTER_STRINGS.inviteCodePlaceholder,
      type: 'text',
    },
  ];

  const handleSubmission = (data: BackrsFormValues) => {
    setIsRegistering(true);

    const transformedData: RegistrationValues = {
      first_name: data.first_name as string,
      last_name: data.last_name as string,
      phone: data.phone as string,
      email: data.email as string,
      password: data.password as string,
      invite_code: data.invite_code ? (data.invite_code as string) : null,
      is_fourteen_plus: !!data.is_fourteen_plus,
      register_id: registerId ?? null,
    };

    handleRegistration(transformedData);
  };

  return (
    <Box sx={containerStyle}>
      <Box sx={responsiveBackrsLogoStyle}>
        <BackrsWithText />
      </Box>

      <Box sx={loginFormStyle}>
        <Typography variant="display-sm-bold" sx={titleStyle}>
          {REGISTER_STRINGS.title}
        </Typography>
        <BackrsForm
          fields={fields}
          onSubmit={handleSubmission}
          buttonLabel={REGISTER_STRINGS.buttonLabel}
          buttonId="register-get-started-button"
          methods={methods}
          isLoading={isRegistering}
        >
          <FormControlLabel
            label={REGISTER_STRINGS.ageCertification}
            sx={checkboxLabelStyle}
            control={
              <Controller
                key="is_fourteen_plus"
                name="is_fourteen_plus"
                control={methods.control}
                rules={{ required: '', validate: (value) => `${value}` === 'true' }} // adding this forces it to be checked
                render={({ field: controllerField }) => (
                  <Checkbox
                    {...controllerField}
                    id="register-age-checkbox"
                    checked={!!controllerField.value}
                    sx={checkboxStyle}
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckIcon />}
                  />
                )}
              />
            }
          />
        </BackrsForm>
        <Box sx={termsAndConditionsContainerStyle}>
          <TermsAndConditions />
        </Box>
        {errorMessage && (
          <Box mt={2} color={({ backrs }) => backrs.colors['Colors/Text/text-error-primary']}>
            {errorMessage}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = ({ backrs }) => ({
  height: '100svh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: {
    xs: backrs.spacing['spacing-lg'],
    sm: backrs.spacing['spacing-2xl'],
  },
});

const titleStyle: SxProps<Theme> = ({ backrs, typography }) => ({
  color: backrs.colors['Colors/Text/text-brand-primary'],
  mb: {
    xs: backrs.spacing['spacing-md'],
    sm: backrs.spacing['spacing-4xl'],
  },
  fontSize: {
    xs: typography['display-xs-bold']?.fontSize,
    sm: typography['display-md-bold']?.fontSize,
  },
});

const loginFormStyle: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  paddingX: backrs.spacing['spacing-md'],
  paddingY: {
    xs: backrs.spacing['spacing-none'],
    sm: backrs.spacing['spacing-3xl'],
  },
  borderRadius: backrs.radius['radius-md'],
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '100%',
  width: '100%',
  boxSizing: 'border-box',
  '@media (min-width: 300px)': {
    paddingX: backrs.spacing['spacing-3xl'],
    paddingY: backrs.spacing['spacing-2xl'],
  },
  '@media (min-width: 600px)': {
    maxWidth: '500px',
    paddingX: backrs.spacing['spacing-8xl'],
    paddingY: backrs.spacing['spacing-7xl'],
  },
  boxShadow: backrs.shadows['shadow-3xl'],
  border: `3px solid ${backrs.colors['Colors/Base/Black']}`,
});

const responsiveBackrsLogoStyle: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  maxWidth: '175px',
  padding: backrs.spacing['spacing-md'],
  paddingTop: backrs.spacing['spacing-2xl'],
  marginBottom: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-2xl'],
  },
});

const checkboxLabelStyle: SxProps<Theme> = ({ backrs }) => ({
  outline: 'none',
  padding: backrs.spacing['spacing-none'],
  alignItems: 'center',
  '.MuiFormControlLabel-label': {
    color: backrs.colors['Colors/Text/text-brand-primary'],
    fontSize: '0.75rem',
  },
});

const checkboxStyle: SxProps<Theme> = ({ backrs }) => ({
  '&.Mui-checked': {
    color: 'white',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.125rem',
    border: `2px solid ${backrs.colors['Colors/Text/text-brand-primary']}`,
    backgroundColor: backrs.colors['Colors/Text/text-brand-primary'],
  },
  padding: '11px 0px 11px 11px',
});

const termsAndConditionsContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-2xl'],
});
